import { MouseEvent, useState } from "react";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import type { SalesCopilotOffering } from "@/apis/emback/offerings";

interface OfferingLinkBoxProps {
  /** The tenant's offerings, or null while they load. */
  offerings: SalesCopilotOffering[] | null;
  /** The chosen offering, or null for none. */
  linkedOfferingId: string | null;
  /** Called with the chosen offering, or null to clear it. */
  onLink: (offeringId: string | null) => void;
}

/**
 * The admin-only choice of the offering a list opens on in Sales Copilot: a
 * button that lists the tenant's offerings, with a way back to none once one
 * is chosen.
 *
 * @param props - See {@link OfferingLinkBoxProps}.
 */
export function OfferingLinkBox({
  offerings,
  linkedOfferingId,
  onLink,
}: OfferingLinkBoxProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const linked = offerings?.find(
    (offering) => offering._id === linkedOfferingId
  );
  const choose = (offeringId: string | null) => {
    onLink(offeringId);
    setAnchorEl(null);
  };
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      data-testid="save-as-workbook-admin-box"
      sx={{
        mt: 1.5,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "grey.50",
      }}
    >
      <Box flex={1} minWidth={0}>
        <Typography
          variant="overline"
          component="p"
          color="text.secondary"
          lineHeight={1.5}
        >
          For admins
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="save-as-workbook-linked-offering-title"
        >
          {linked
            ? `Opens on ${linked.name} in Sales Copilot.`
            : "Choose the offering this list opens on in Sales Copilot."}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={(event: MouseEvent<HTMLElement>) =>
          setAnchorEl(event.currentTarget)
        }
        disabled={!offerings?.length}
        data-testid="save-as-workbook-link-offering-btn"
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {linked ? "Change offering" : "Link to offering"}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        data-testid="save-as-workbook-offering-menu"
      >
        {linked && (
          <MenuItem
            onClick={() => choose(null)}
            data-testid="save-as-workbook-offering-option-none"
          >
            <ListItemText inset>No offering</ListItemText>
          </MenuItem>
        )}
        {(offerings ?? []).map((offering) => {
          const isLinked = offering._id === linkedOfferingId;
          return (
            <MenuItem
              key={offering._id}
              selected={isLinked}
              onClick={() => choose(offering._id)}
              data-testid={`save-as-workbook-offering-option-${offering._id}`}
            >
              {isLinked && (
                <ListItemIcon>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText inset={!isLinked}>{offering.name}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Stack>
  );
}
